/**
 * PDP buy box interactivity. Figma 624:595.
 *
 * <pdp-gallery>: thumbnail rail + prev/next arrows + NN/NN counter (627:1186).
 * <pdp-purchase-options>: one-time vs subscribe radios feeding the hidden
 * selling_plan input (627:1206); interval <select> updates plan id and price.
 * <pdp-kit-variants>: "Chose Your Kit" cards on a multi-variant kit (1484:377),
 *   which also drive which Kit Includes rows are listed (1484:444-466).
 *
 * No framework: product-form.js (Horizon) already handles the form submit.
 */
(() => {
  'use strict';

  class PdpGallery extends HTMLElement {
    connectedCallback() {
      // Stage images only — the lightbox has its own single <img> that mirrors
      // the active stage image, so it must stay out of this collection.
      this.images = Array.from(this.querySelectorAll('.pdp-gallery__stage .pdp-gallery__image'));
      this.thumbs = Array.from(this.querySelectorAll('.pdp-gallery__thumb'));
      this.counters = Array.from(this.querySelectorAll('[data-counter]'));
      this.lightbox = this.querySelector('[data-lightbox]');
      this.lightboxImage = this.querySelector('[data-lightbox-image]');
      this.index = 0;

      // Stage and lightbox arrows share the class and the handler.
      this.querySelectorAll('.pdp-gallery__arrow').forEach((arrow) => {
        arrow.addEventListener('click', () => this.show(this.index + Number(arrow.dataset.dir)));
      });
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.show(Number(thumb.dataset.index)));
      });

      // Lightbox: click the stage photo to open; Esc (native), the close
      // button, or a click on the backdrop (the dialog element itself, since
      // its content is what fills the box) closes it.
      const stage = this.querySelector('.pdp-gallery__stage');
      stage?.addEventListener('click', (event) => {
        if (event.target.closest('.pdp-gallery__image')) this.openLightbox();
      });
      this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.lightbox?.close());
      this.lightbox?.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.lightbox.close();
      });

      // Slides after the first ship their files in data-src/data-srcset so they
      // stop competing with the first one while the page loads (they are
      // stacked under it, so loading="lazy" never deferred them). Attach them
      // once the page has loaded, or as soon as the shopper reaches for the
      // gallery, whichever comes first.
      const attachAll = () => this.images.forEach((img) => this.attach(img));
      const whenIdle = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(attachAll, { timeout: 2000 });
        else window.setTimeout(attachAll, 200);
      };
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true });
      ['pointerenter', 'focusin', 'touchstart'].forEach((type) => {
        this.addEventListener(type, attachAll, { once: true, passive: true });
      });
    }

    attach(img) {
      if (!img) return;
      const { srcset, src } = img.dataset;
      if (srcset) {
        img.srcset = srcset;
        delete img.dataset.srcset;
      }
      if (src) {
        img.src = src;
        delete img.dataset.src;
      }
    }

    show(index) {
      const count = this.images.length;
      if (!count) return;
      this.index = ((index % count) + count) % count;
      this.attach(this.images[this.index]);
      this.images.forEach((img, i) => img.classList.toggle('is-active', i === this.index));
      this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === this.index));
      const pad = (n) => String(n).padStart(2, '0');
      this.counters.forEach((counter) => {
        counter.textContent = `${pad(this.index + 1)}/${pad(count)}`;
      });
      if (this.lightbox?.open) this.syncLightboxImage();
    }

    syncLightboxImage() {
      const active = this.images[this.index];
      if (!active || !this.lightboxImage) return;
      this.lightboxImage.src = active.dataset.zoom || active.currentSrc || active.src;
      this.lightboxImage.alt = active.alt || '';
    }

    openLightbox() {
      if (!this.lightbox) return;
      this.syncLightboxImage();
      this.lightbox.showModal();
    }
  }

  class PdpPurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.suppressLegacyRechargeWidget();

      this.planInput = this.querySelector('[data-selling-plan-input]');
      this.options = Array.from(this.querySelectorAll('.pdp-purchase-options__option'));
      this.planSelect = this.querySelector('[data-plan-select]');
      this.planPrice = this.querySelector('[data-plan-price]');

      this.options.forEach((option) => {
        const radio = option.querySelector('input[type="radio"]');
        radio?.addEventListener('change', () => this.select(option));
        // The whole row is a <label>, so clicking anywhere fires the radio.
      });

      this.planSelect?.addEventListener('change', () => {
        const chosen = this.planSelect.selectedOptions[0];
        if (!chosen) return;
        const subscribeRadio = this.querySelector('[data-option="subscribe"] input[type="radio"]');
        if (subscribeRadio) subscribeRadio.value = chosen.value;
        if (this.planPrice && chosen.dataset.price) this.planPrice.textContent = chosen.dataset.price;
        if (this.planInput && this.querySelector('[data-option="subscribe"]')?.classList.contains('is-selected')) {
          this.planInput.value = chosen.value;
        }
        this.mirrorPrice();
      });
    }

    select(option) {
      this.options.forEach((o) => o.classList.toggle('is-selected', o === option));
      const radio = option.querySelector('input[type="radio"]');
      if (this.planInput) this.planInput.value = radio ? radio.value : '';
      this.mirrorPrice();
    }

    /**
     * The mobile buy row and the sticky bar print the one-time price, which is
     * what their proxied add-to-cart posts by default. Once Subscribe & Save is
     * picked the same button posts the plan, so both show the plan price (the
     * one-time price struck beside it in the row); picking one-time again puts
     * the server-rendered markup back. Text is copied with textContent, never
     * parsed as HTML.
     */
    mirrorPrice() {
      const section = this.closest('.shopify-section');
      if (!section) return;
      const selected = this.options.find((o) => o.classList.contains('is-selected'));
      const subscribed = selected?.dataset.option === 'subscribe';
      const planPrice = subscribed ? selected.querySelector('[data-plan-price]')?.textContent.trim() : '';
      const oneTime = subscribed ? selected.querySelector('.pdp-purchase-options__compare')?.textContent.trim() : '';

      section.querySelectorAll('[data-price-mirror]').forEach((node) => {
        if (!PdpPurchaseOptions.originals.has(node)) PdpPurchaseOptions.originals.set(node, node.innerHTML);
        if (!subscribed || !planPrice) {
          node.innerHTML = PdpPurchaseOptions.originals.get(node);
          return;
        }
        node.replaceChildren();
        if (node.dataset.priceMirror === 'sticky') {
          const price = document.createElement('span');
          price.className = 'price';
          price.textContent = planPrice;
          node.append(price);
          return;
        }
        if (oneTime) {
          const compare = document.createElement('s');
          compare.className = 'pdp-kit-total__compare';
          compare.textContent = oneTime;
          node.append(compare);
        }
        const price = document.createElement('span');
        price.className = 'pdp-kit-total__price';
        price.textContent = planPrice;
        node.append(price);
      });
    }

    /**
     * ReCharge ships a second one-time/subscribe picker into this same form.
     * It arrives from a store-wide Online Store script tag
     * (static.rechargecdn.com/assets/js/widget.min.js, emitted inside
     * `content_for_header`) rather than from a theme app-embed block, so the
     * theme cannot decline it — it has to be taken back out here.
     *
     * Hiding it in CSS is not sufficient. While its "subscribe" option is
     * active ReCharge puts `name="selling_plan"` on its own frequency
     * <select>, and a field inside a `display: none` subtree is still
     * serialised, so the form would post two `selling_plan` values and the
     * winning one is whichever the platform reads last. Removing the widget
     * from the DOM is what keeps the line item deterministic; the CSS rule in
     * the section only covers the frames before this runs.
     *
     * `ReChargeWidget.destroyAllWidgets()` is ReCharge's own public entry
     * point and unmounts their Vue app cleanly (verified: wrapper gone, our
     * selector untouched, no console errors). The DOM sweep behind it is the
     * fallback for the day that API changes.
     */
    suppressLegacyRechargeWidget() {
      const form = this.closest('form');
      if (!form) return;

      const removeWidget = () => {
        try {
          window.ReChargeWidget?.destroyAllWidgets?.();
        } catch (error) {
          // ReCharge's API moved; the DOM removal below still holds the line.
        }
        form.querySelectorAll('.rc-container-wrapper').forEach((node) => node.remove());
      };

      removeWidget();

      // The script tag loads async — normally after this element upgrades —
      // and ReCharge re-creates its widget on variant change, so this form
      // stays watched for the life of the page.
      this.rechargeObserver = new MutationObserver((records) => {
        const injected = records.some((record) =>
          Array.from(record.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              (node.matches?.('.rc-container-wrapper') || node.querySelector?.('.rc-container-wrapper'))
          )
        );
        if (injected) removeWidget();
      });
      this.rechargeObserver.observe(form, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.rechargeObserver?.disconnect();
    }
  }

  /** Server-rendered markup of each price mirror, restored on one-time. */
  PdpPurchaseOptions.originals = new WeakMap();

  /**
   * <pdp-mobile-buy>: the phone-only price + add-to-cart row under the main
   * photo (client fix plan item 4). Its button owns no form: it clicks this
   * buy box's real add-to-cart, the same proxy the sticky bar uses, so the
   * purchase option, selling plan, quantity and kit variant post exactly as
   * the main form would. `data-puppet` tells Horizon's add-to-cart component
   * not to fly an image from a button that is off screen; the sticky bar's
   * listener and the one here both clear it once the cart answers.
   */
  class PdpMobileBuy extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-mobile-atc]');
      this.button?.addEventListener('click', () => this.add());
    }

    add() {
      const box = this.closest('.pdp-buy-box');
      const target = box?.querySelector('.pdp-buy-box__form [ref="addToCartButton"]');
      if (!target || target.disabled || !this.button) return;

      const done = (event) => {
        document.removeEventListener('shopify:cart:lines-update', done);
        document.removeEventListener('shopify:cart:error', done);
        clearTimeout(this.timer);
        target.dataset.puppet = 'false';
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
        // A failed add prints its message inside the main form, which is
        // further down the page on a phone: bring it into view.
        if (event?.type === 'shopify:cart:error') {
          box.querySelector('.pdp-buy-box__form')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      };
      document.addEventListener('shopify:cart:lines-update', done);
      document.addEventListener('shopify:cart:error', done);
      this.timer = setTimeout(done, 8000);

      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      target.dataset.puppet = 'true';
      target.click();
    }
  }

  /**
   * <pdp-kit-variants>
   *
   * The "Chose Your Kit" selector on a kit with more than one variant. Kit mode
   * renders no other variant control, so before this existed only the first
   * variant of the Starter Kit could be added to the cart.
   *
   * The cards are real links to ?variant=<id>, so the no-JS path is a normal
   * page load that the server renders correctly. This class only upgrades that
   * to an in-page swap: nothing here formats money or invents a price. Every
   * price the shopper can see was rendered by Liquid for every variant up front
   * and is switched with [hidden] — the Total row (.pdp-kit-total__variant) and
   * the sticky bar (.sticky-add-to-cart__price-variant).
   *
   * What it changes on select: the form's hidden `id` input (product-form.js
   * submits `new FormData(form)`, so setting .value is the whole contract), the
   * card's selected state, both price groups, the sticky bar's
   * data-current-variant-id, and the address bar.
   *
   * An UNAVAILABLE variant is deliberately NOT handled here: its sold-out button
   * state and data-variant-available come from the server, so the click falls
   * through to the link and the page reloads.
   */
  class PdpKitVariants extends HTMLElement {
    /**
     * Pure: the URL the address bar should carry for a variant. Kept static and
     * side-effect free so it can be exercised without a browser.
     */
    static variantUrl(href, variantId) {
      const url = new URL(href, 'https://emujoy.invalid');
      url.searchParams.set('variant', String(variantId));
      return url.pathname + url.search + url.hash;
    }

    /**
     * Pure enough to unit test: show the node whose data-variant-id matches and
     * hide the rest. Returns how many matched, so a selector that has drifted
     * away from the markup reports 0 instead of silently hiding everything.
     */
    static toggle(nodes, variantId) {
      const id = String(variantId);
      let matched = 0;
      for (const node of nodes) {
        const on = node.dataset.variantId === id;
        node.hidden = !on;
        if (on) matched += 1;
      }
      return matched;
    }

    /**
     * Pure: the option values a card's variant is made of, as Liquid wrote them
     * into data-variant-options. Returns [] on anything unreadable so the caller
     * can decline to touch the rows rather than guess at them.
     */
    static variantOptionsOf(card) {
      try {
        const parsed = JSON.parse(card.dataset.variantOptions || '[]');
        return Array.isArray(parsed) ? parsed.map(String) : [];
      } catch (error) {
        return [];
      }
    }

    /**
     * Pure: show the Kit Includes rows this variant actually ships. A row is in
     * `rows` only if Liquid gave it a data-variant-only, i.e. only if it is
     * conditional -- a row belonging to every variant carries no attribute, is
     * never collected, and so can never be hidden here. Returns how many rows
     * were shown, so a selector that drifts away from the markup reports 0
     * instead of quietly emptying the box.
     */
    static toggleRows(rows, optionValues) {
      let shown = 0;
      for (const row of rows) {
        const on = optionValues.includes(row.dataset.variantOnly);
        row.hidden = !on;
        if (on) shown += 1;
      }
      return shown;
    }

    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-variant-id]'));
      if (this.cards.length < 2) return;

      const buyBox = this.closest('.pdp-buy-box');
      // The sticky bar is rendered by the same section but OUTSIDE .pdp-buy-box,
      // so the two lookups have deliberately different roots.
      const section = this.closest('.shopify-section') || document;

      // The add-on and upsell mini-forms are .pdp-buy-box__routine-form, so this
      // resolves to the kit's own form and not to one of theirs.
      this.variantInput = buyBox?.querySelector('.pdp-buy-box__form input[name="id"]') || null;
      this.totals = Array.from(section.querySelectorAll('.pdp-kit-total__variant[data-variant-id]'));
      this.stickyPrices = Array.from(section.querySelectorAll('.sticky-add-to-cart__price-variant[data-variant-id]'));
      this.stickyRoot = section.querySelector('sticky-add-to-cart');

      // Kit Includes rows that belong to one variant (Starter's On The Go Stick
      // is on the Plus only). The server already renders these correctly for
      // the default and for ?variant=, so everything here is an upgrade of a
      // page that is already right.
      this.kitRows = Array.from(section.querySelectorAll('.pdp-kit-includes__row[data-variant-only]'));
      this.cardOptions = new Map(
        this.cards.map((card) => [card.dataset.variantId, PdpKitVariants.variantOptionsOf(card)])
      );
      // If any card's option values are unreadable, a conditional row would be
      // hidden on every variant with nothing on screen to explain the missing
      // product -- so the click falls through to the link and the SERVER keeps
      // rendering the rows, the same escape the unavailable variant takes.
      this.canSwapRows = this.cards.every(
        (card) => (this.cardOptions.get(card.dataset.variantId) || []).length > 0
      );

      this.addEventListener('click', (event) => {
        const card = event.target.closest('[data-variant-id]');
        if (!card || !this.contains(card)) return;
        if (card.dataset.available !== 'true') return; // let the server render it
        if (this.kitRows.length && !this.canSwapRows) return; // ditto
        event.preventDefault();
        this.select(card.dataset.variantId);
      });

      // Initial load / ?variant=: idempotent with what Liquid rendered, and it
      // is what keeps the rows right if anything re-mounts this element.
      if (this.kitRows.length && this.canSwapRows) {
        const selected = this.cards.find((card) => card.classList.contains('is-selected')) || this.cards[0];
        PdpKitVariants.toggleRows(this.kitRows, this.cardOptions.get(selected.dataset.variantId) || []);
      }
    }

    select(variantId) {
      const id = String(variantId);

      for (const card of this.cards) {
        const on = card.dataset.variantId === id;
        card.classList.toggle('is-selected', on);
        if (on) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');
      }

      PdpKitVariants.toggle(this.totals, id);
      PdpKitVariants.toggle(this.stickyPrices, id);
      if (this.kitRows.length) PdpKitVariants.toggleRows(this.kitRows, this.cardOptions.get(id) || []);

      if (this.variantInput) this.variantInput.value = id;
      if (this.stickyRoot) this.stickyRoot.dataset.currentVariantId = id;

      // replaceState, not pushState: a kit selection is not a page the shopper
      // should have to press Back through, but a copied URL must reproduce it.
      window.history.replaceState({}, '', PdpKitVariants.variantUrl(window.location.href, id));
    }
  }

  if (!customElements.get('pdp-gallery')) customElements.define('pdp-gallery', PdpGallery);
  if (!customElements.get('pdp-purchase-options')) customElements.define('pdp-purchase-options', PdpPurchaseOptions);
  if (!customElements.get('pdp-kit-variants')) customElements.define('pdp-kit-variants', PdpKitVariants);
  if (!customElements.get('pdp-mobile-buy')) customElements.define('pdp-mobile-buy', PdpMobileBuy);
})();
